import {
  List,
  Datagrid,
  FunctionField,
  RaRecord,
} from 'react-admin';
import { DateTimeField } from '../../shared/DateFields';
import { Alert, Box, Chip, Typography } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import {
  ClampedText,
  InlinePair,
  compactDatagridSx,
} from '../../shared/listCells';
import { menuLabel } from '../menuConfig';
import { ConnectionChecks } from './ConnectionChecks';
import { FeatureGate } from '../featureFlags/FeatureGate';
import { ViaSftpCredentialsForm } from './ViaSftpCredentialsForm';

type CredentialRecord = RaRecord & Record<string, unknown>;

/**
 * Wartość poświadczenia — ZAWSZE maska.
 *
 * Nie ma przycisku „pokaż". Taka trasa była w bazie kodu, z której portujemy,
 * i świadomie jej nie przenieśliśmy: wygodna, a zamienia magazyn sekretów
 * w ich wyświetlacz. Sekret opuszcza magazyn wyłącznie po stronie serwera,
 * w kodzie integracji, i zostawia wtedy ślad w dzienniku dostępu.
 */
const MaskedValue = () => (
  <Chip size="small" variant="outlined" icon={<LockIcon />} label="••••••••" />
);

const LocationCell = ({ record }: { record: CredentialRecord }) => (
  <InlinePair
    primary={String(record.system ?? '—')}
    secondary={`${String(record.field ?? '—')}${
      record.companyId != null ? ` · spółka nr ${String(record.companyId)}` : ' · wspólne'
    }`}
    maxWidth={{ xs: 340, xl: 440 }}
  />
);

/** Baner o szyfrowaniu i maskach — nad listą i przy pustej liście. */
export const EncryptionNotice = () => (
  <Alert severity="info" sx={{ mb: 2 }}>
    Wartości są szyfrowane i nie da się ich tu odczytać — także
    administratorowi. Kod integracji pobiera je po stronie serwera, a każde
    takie pobranie zostawia wpis w dzienniku dostępu.
  </Alert>
);

/**
 * Pusta lista. Domyślny ekran react-admina zastępuje całą treść listy, więc
 * przy braku wpisów znikał baner i zostawało samo „Brak wpisów” (e2e na dev,
 * 28.09.2026) — nie wiadomo było, czy to błąd, czy stan.
 */
export const CredentialsEmpty = () => (
  <Box sx={{ p: 2 }}>
    <EncryptionNotice />
    {/* Bez wpisu w magazynie sprawdzenie i tak ma sens: powie „brak poświadczenia”.
        Za przełącznikiem `ecoTruckConnectionCheck` (12.10.2026). */}
    <FeatureGate feature="ecoTruckConnectionCheck">
      <ConnectionChecks />
    </FeatureGate>
    {/* Login i hasło serwera SFTP importu TMS (15.10.2026) — tylko zapis. */}
    <ViaSftpCredentialsForm />
    <Typography variant="body2" color="text.secondary">
      Brak poświadczeń — wprowadza je administrator po stronie serwera (magazyn
      zaszyfrowany, klucz poza bazą). Ten ekran nie pokazuje wartości; wpisać można tu
      tylko login i hasło serwera SFTP importu TMS.
    </Typography>
  </Box>
);

/**
 * Poświadczenia integracji.
 *
 * Widoczne wyłącznie dla ról zarządzających — o dostępie rozstrzyga API.
 */
export const CredentialsList = () => (
  <List
    perPage={50}
    title={menuLabel('credentials')}
    pagination={false}
    actions={false}
    empty={<CredentialsEmpty />}
  >
    <>
      <EncryptionNotice />
      <FeatureGate feature="ecoTruckConnectionCheck">
        <ConnectionChecks />
      </FeatureGate>
      <ViaSftpCredentialsForm />
      <Datagrid bulkActionButtons={false} rowClick={false} sx={compactDatagridSx}>
        <FunctionField label="Integracja" render={(r: RaRecord) => <LocationCell record={r} />} />
        <FunctionField label="Wartość" render={() => <MaskedValue />} />
        <FunctionField
          source="description"
          label="Do czego służy"
          render={(r: RaRecord) => (
            <ClampedText text={r.description as string | undefined} maxWidth={{ xs: 380, xl: 640 }} />
          )}
        />
        <DateTimeField source="updatedAt" label="Ostatnia zmiana" />
      </Datagrid>
    </>
  </List>
);
